// ChatWidget.js - AI-powered Sales & Marketing Chatbot Widget
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ThumbsUp, ThumbsDown, Sparkles, ChevronDown, Search, Shield, Smartphone, Briefcase, ShoppingBag, Zap, Home, User, Mail, Phone } from 'lucide-react';
import './ChatWidget.css';

const API_BASE = '/api/chat';

// Helper to get CSRF token
const getCSRFToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
};

// Detect page context from URL
const getPageContext = () => {
    const path = window.location.pathname.toLowerCase();
    let action = '';
    let hint = '';

    if (path.includes('/add-business') || path.includes('/add-listing')) {
        action = 'listing_business';
        hint = 'User is adding a business listing';
    } else if (path.includes('/job')) {
        action = 'posting_job';
        hint = 'User is viewing/creating job listings';
    } else if (path.includes('/deal')) {
        action = 'browsing_deals';
        hint = 'User is browsing deals';
    } else if (path.includes('/classified')) {
        action = 'classifieds';
        hint = 'User is in classifieds';
    } else if (path.includes('/realestate') || path.includes('/real-estate')) {
        action = 'real_estate';
        hint = 'User is browsing real estate';
    } else if (path.includes('/ai-tool')) {
        action = 'ai_tools';
        hint = 'User is exploring AI tools';
    } else if (path.includes('/product-search')) {
        action = 'product_search';
        hint = 'User is searching products';
    } else if (path.includes('/dashboard')) {
        action = 'dashboard';
        hint = 'User is in dashboard';
    }

    return { page: window.location.pathname, action, hint };
};

// Parse inline formatting (bold and links)
const parseInlineFormatting = (text, keyPrefix = '') => {
    if (!text) return null;

    const elements = [];
    let remaining = text;
    let partIndex = 0;

    // Regex to match markdown links [text](url) or bold **text**
    const pattern = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            elements.push(text.slice(lastIndex, match.index));
        }

        if (match[2] && match[3]) {
            // It's a link [text](url)
            const linkText = match[2];
            const linkUrl = match[3];
            elements.push(
                <a
                    key={`${keyPrefix}-link-${partIndex}`}
                    href={linkUrl}
                    className="chat-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        // Open all links in new tab to keep chat open
                        e.preventDefault();
                        window.open(linkUrl, '_blank');
                    }}
                >
                    {linkText}
                </a>
            );
        } else if (match[4]) {
            // It's bold **text**
            elements.push(<strong key={`${keyPrefix}-bold-${partIndex}`}>{match[4]}</strong>);
        }

        lastIndex = pattern.lastIndex;
        partIndex++;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
        elements.push(text.slice(lastIndex));
    }

    return elements.length > 0 ? elements : text;
};

// Format message content with markdown-like styling
const formatMessageContent = (content) => {
    if (!content) return null;

    // Split by newlines
    const lines = content.split('\n');

    return lines.map((line, lineIndex) => {
        // Check if line is a bullet point
        const isBullet = line.trim().startsWith('•') || line.trim().startsWith('-');
        const bulletContent = isBullet ? line.trim().replace(/^[•\-]\s*/, '') : line;

        // Parse inline formatting (links and bold)
        const formattedParts = parseInlineFormatting(isBullet ? bulletContent : line, `line-${lineIndex}`);

        if (isBullet) {
            return (
                <div key={lineIndex} className="message-bullet">
                    <span className="bullet-dot">•</span>
                    <span className="bullet-text">{formattedParts}</span>
                </div>
            );
        }

        // Empty line = paragraph break
        if (line.trim() === '') {
            return <div key={lineIndex} className="message-break" />;
        }

        return <div key={lineIndex} className="message-line">{formattedParts}</div>;
    });
};

// Message component
const ChatMessageBubble = ({ message, onFeedback }) => {
    const isUser = message.role === 'user';
    const [feedbackGiven, setFeedbackGiven] = useState(message.feedback);

    const handleFeedback = async (type) => {
        if (feedbackGiven) return;
        setFeedbackGiven(type);
        if (onFeedback && message.id) {
            onFeedback(message.id, type);
        }
    };

    return (
        <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
            {!isUser && (
                <div className="message-avatar">
                    <Sparkles size={16} />
                </div>
            )}
            <div className="message-content">
                <div className="message-text">
                    {isUser ? message.content : formatMessageContent(message.content)}
                </div>
                {!isUser && message.id && (
                    <div className="message-feedback">
                        <button
                            className={`feedback-btn ${feedbackGiven === 'helpful' ? 'active' : ''}`}
                            onClick={() => handleFeedback('helpful')}
                            disabled={!!feedbackGiven}
                            title="Helpful"
                        >
                            <ThumbsUp size={12} />
                        </button>
                        <button
                            className={`feedback-btn ${feedbackGiven === 'not_helpful' ? 'active' : ''}`}
                            onClick={() => handleFeedback('not_helpful')}
                            disabled={!!feedbackGiven}
                            title="Not helpful"
                        >
                            <ThumbsDown size={12} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Typing indicator
const TypingIndicator = () => (
    <div className="chat-message assistant">
        <div className="message-avatar">
            <Sparkles size={16} />
        </div>
        <div className="message-content">
            <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>
    </div>
);

// Lead capture form component
const LeadCaptureForm = ({ sessionId, onSubmitted, onSkip }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) return;
        setSubmitting(true);
        try {
            const response = await fetch(`${API_BASE}/lead/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken(),
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    name: name.trim(),
                    email: email.trim(),
                    phone: phone.trim(),
                }),
            });
            const data = await response.json();
            if (data.success) {
                setSubmitted(true);
                if (onSubmitted) onSubmitted();
            }
        } catch (err) {
            console.error('Error submitting lead:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="lead-form-success">
                <span>Thank you! We'll follow up soon.</span>
            </div>
        );
    }

    return (
        <div className="lead-capture-form">
            <div className="lead-form-header">
                Want us to follow up with you?
            </div>
            <form onSubmit={handleSubmit}>
                <div className="lead-form-row">
                    <div className="lead-input-wrapper">
                        <User size={14} />
                        <input
                            type="text"
                            placeholder="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="lead-input-wrapper">
                        <Mail size={14} />
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                </div>
                <div className="lead-form-row">
                    <div className="lead-input-wrapper">
                        <Phone size={14} />
                        <input
                            type="tel"
                            placeholder="Phone (optional)"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </div>
                </div>
                <div className="lead-form-actions">
                    <button type="submit" className="lead-submit-btn" disabled={submitting}>
                        {submitting ? 'Sending...' : 'Submit'}
                    </button>
                    <button type="button" className="lead-skip-btn" onClick={onSkip}>
                        Skip
                    </button>
                </div>
            </form>
        </div>
    );
};

// Suggested question button (kept for potential future use)
// const SuggestedQuestion = ({ question, onClick }) => (
//     <button className="suggested-question" onClick={() => onClick(question)}>
//         {question}
//     </button>
// );

// Welcome suggestion card with icon
const WelcomeSuggestionCard = ({ icon: Icon, title, description, onClick }) => (
    <button className="welcome-card" onClick={onClick}>
        <div className="welcome-card-icon">
            <Icon size={20} />
        </div>
        <div className="welcome-card-content">
            <span className="welcome-card-title">{title}</span>
            {description && <span className="welcome-card-desc">{description}</span>}
        </div>
    </button>
);

// Welcome screen component
const WelcomeScreen = ({ onStartChat, onSuggestionClick, isOfficeHours }) => {
    const suggestions = [
        {
            icon: Zap,
            title: "What makes this unique?",
            description: "Discover our special features",
            question: "What makes this platform unique?"
        },
        {
            icon: Home,
            title: "Real Estate",
            description: "List properties FREE",
            question: "Tell me about Real Estate features"
        },
        {
            icon: Search,
            title: "AI Image Search",
            description: "Find products by photo",
            question: "How does AI image search work?"
        },
        {
            icon: ShoppingBag,
            title: "For Business Owners",
            description: "List your business FREE",
            question: "I own a business - why should I join?"
        },
        {
            icon: Smartphone,
            title: "FREE AI Tools",
            description: "Calculators & planners",
            question: "Show me FREE AI tools"
        },
        {
            icon: Briefcase,
            title: "Jobs & Deals",
            description: "Find work, save more",
            question: "What jobs are available?"
        }
    ];

    return (
        <div className="welcome-screen">
            <div className="welcome-header">
                <div className="welcome-avatar">
                    <Sparkles size={24} />
                </div>
                <div className="welcome-title-section">
                    <h3 className="welcome-title">AI Assistant Ready!</h3>
                    <span className="welcome-status">
                        <span className={`status-dot ${isOfficeHours ? '' : 'away'}`}></span>
                        {isOfficeHours
                            ? 'Desi Firms • Online Now'
                            : 'AI available • Team back during business hours'
                        }
                    </span>
                </div>
            </div>

            <div className="welcome-help-text">
                <span className="lightbulb">💡</span> I can help you with:
            </div>

            <div className="welcome-suggestions">
                {suggestions.map((item, index) => (
                    <WelcomeSuggestionCard
                        key={index}
                        icon={item.icon}
                        title={item.title}
                        description={item.description}
                        onClick={() => onSuggestionClick(item.question)}
                    />
                ))}
            </div>

            <button className="welcome-start-btn" onClick={onStartChat}>
                <Sparkles size={18} />
                Start Chatting Now
            </button>
        </div>
    );
};

// Check if mobile/small screen
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 480);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return isMobile;
};

// Main Chat Widget Component
const ChatWidget = ({ onMobileNudgeClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showWelcome, setShowWelcome] = useState(true); // Show welcome screen first
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const [showMobileNudge, setShowMobileNudge] = useState(false);
    const [isOfficeHours, setIsOfficeHours] = useState(true);
    const [showLeadForm, setShowLeadForm] = useState(false);
    const [leadCaptured, setLeadCaptured] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const isMobile = useIsMobile();

    // Load session from localStorage
    useEffect(() => {
        const savedSessionId = localStorage.getItem('chat_session_id');
        if (savedSessionId) {
            setSessionId(savedSessionId);
        }
        // Check if lead was already captured
        const savedLeadCaptured = localStorage.getItem('chat_lead_captured');
        if (savedLeadCaptured) {
            setLeadCaptured(true);
        }
    }, []);

    // Start or resume chat session
    const startChat = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/start/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken(),
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    source_page: window.location.pathname,
                }),
            });

            const data = await response.json();

            if (data.session_id) {
                setSessionId(data.session_id);
                localStorage.setItem('chat_session_id', data.session_id);
            }

            if (data.messages) {
                setMessages(data.messages.map(msg => ({
                    ...msg,
                    id: msg.id || `msg-${Date.now()}-${Math.random()}`,
                })));
            }

            if (data.is_office_hours !== undefined) {
                setIsOfficeHours(data.is_office_hours);
            }
        } catch (error) {
            console.error('Error starting chat:', error);
            // Show default welcome message
            setMessages([{
                id: 'welcome',
                role: 'assistant',
                content: "Hello! Welcome to our South Asian community hub. I'm here to help you explore features, AI tools, and more. How can I assist you today?",
            }]);
        }
    }, [sessionId]);

    // Auto-open chat on first visit after a delay
    // On mobile: show a small nudge bubble instead of opening the full chat
    useEffect(() => {
        const hasSeenChat = sessionStorage.getItem('chat_auto_opened');

        if (!hasSeenChat && !hasAutoOpened) {
            const timer = setTimeout(() => {
                if (isMobile) {
                    // Mobile: show a small nudge bubble
                    setShowMobileNudge(true);
                }
                setHasAutoOpened(true);
                sessionStorage.setItem('chat_auto_opened', 'true');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [hasAutoOpened, startChat, isMobile]);

    // Scroll to bottom when messages change or suggestions hide
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, showLeadForm]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && !isMinimized && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, isMinimized]);

    // Open chat
    const handleOpen = () => {
        setIsOpen(true);
        setIsMinimized(false);
        setShowMobileNudge(false);
        // Show welcome screen if no messages yet
        if (messages.length === 0) {
            setShowWelcome(true);
        }
    };

    // Dismiss mobile nudge
    const handleDismissNudge = (e) => {
        e.stopPropagation();
        setShowMobileNudge(false);
    };

    // Handle starting chat from welcome screen
    const handleStartFromWelcome = () => {
        setShowWelcome(false);
        startChat();
    };

    // Handle suggestion click from welcome screen
    const handleWelcomeSuggestion = (question) => {
        setShowWelcome(false);
        startChat().then(() => {
            // Small delay to ensure session is created
            setTimeout(() => sendMessage(question), 500);
        });
    };

    // Close chat
    const handleClose = () => {
        setIsOpen(false);
    };

    // Toggle minimize
    const handleMinimize = () => {
        setIsMinimized(!isMinimized);
    };

    // Send message
    const sendMessage = async (messageText = inputValue) => {
        const text = messageText.trim();
        if (!text || isLoading) return;

        setInputValue('');
        setIsLoading(true);

        // Add user message immediately
        const userMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: text,
        };
        setMessages(prev => [...prev, userMessage]);

        try {
            // Build request with page context
            const pageContext = getPageContext();
            const response = await fetch(`${API_BASE}/message/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken(),
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    message: text,
                    page: pageContext.page,
                    action: pageContext.action,
                    hint: pageContext.hint,
                }),
            });

            const data = await response.json();

            if (data.response) {
                const assistantMessage = {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: data.response,
                };
                setMessages(prev => [...prev, assistantMessage]);
            }

            if (data.is_office_hours !== undefined) {
                setIsOfficeHours(data.is_office_hours);
            }

            // Show lead form if backend says so and not already captured
            if (data.show_lead_form && !leadCaptured) {
                setShowLeadForm(true);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => [...prev, {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: "I apologize, but I'm having trouble responding right now. Please try again!",
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle lead form submission
    const handleLeadSubmitted = () => {
        setLeadCaptured(true);
        setShowLeadForm(false);
        localStorage.setItem('chat_lead_captured', 'true');
    };

    // Handle lead form skip
    const handleLeadSkip = () => {
        setShowLeadForm(false);
        setLeadCaptured(true); // Don't show again
        localStorage.setItem('chat_lead_captured', 'true');
    };

    // Handle input submit
    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage();
    };

    // Handle feedback
    const handleFeedback = async (messageId, feedbackType) => {
        try {
            await fetch(`${API_BASE}/feedback/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCSRFToken(),
                },
                body: JSON.stringify({
                    message_id: messageId,
                    feedback: feedbackType,
                }),
            });
        } catch (error) {
            console.error('Error submitting feedback:', error);
        }
    };

    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            {/* Mobile Nudge Bubble - tapping opens the "How to Add Your Business" guide */}
            {showMobileNudge && !isOpen && (
                <div className="chat-mobile-nudge" onClick={() => {
                    setShowMobileNudge(false);
                    if (onMobileNudgeClick) onMobileNudgeClick();
                    else handleOpen();
                }}>
                    <div className="nudge-avatar">
                        <Sparkles size={16} />
                    </div>
                    <div className="nudge-text">
                        <span className="nudge-greeting">Add your business?</span>
                        <span className="nudge-message">Tap for step-by-step guide</span>
                    </div>
                    <button
                        className="nudge-close"
                        onClick={handleDismissNudge}
                        aria-label="Dismiss"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Desktop guide hint — always visible above Ask AI button */}
            {!isOpen && (
                <button
                    className="chat-desktop-hint"
                    onClick={() => { if (onMobileNudgeClick) onMobileNudgeClick(); }}
                    aria-label="Open business listing guide"
                >
                    <span className="desktop-hint-title">Need guidance?</span>
                    <span className="desktop-hint-sub">How to list your business →</span>
                </button>
            )}

            {/* Floating Chat Button */}
            {!isOpen && (
                <button
                    className="chat-floating-button"
                    onClick={handleOpen}
                    aria-label="Open chat"
                >
                    <MessageCircle size={24} />
                    <span className="chat-button-text">Ask AI</span>
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div className={`chat-widget ${isMinimized ? 'minimized' : ''} ${showWelcome ? 'welcome-mode' : ''}`}>
                    {/* Close button for welcome mode */}
                    {showWelcome && (
                        <button
                            className="welcome-close-btn"
                            onClick={handleClose}
                            aria-label="Close chat"
                        >
                            <X size={18} />
                        </button>
                    )}

                    {/* Welcome Screen */}
                    {showWelcome && !isMinimized && (
                        <WelcomeScreen
                            onStartChat={handleStartFromWelcome}
                            onSuggestionClick={handleWelcomeSuggestion}
                            isOfficeHours={isOfficeHours}
                        />
                    )}

                    {/* Chat Mode */}
                    {!showWelcome && (
                        <>
                            {/* Header */}
                            <div className="chat-header" onClick={isMinimized ? handleMinimize : undefined}>
                                <div className="chat-header-info">
                                    <div className="chat-header-avatar">
                                        <Sparkles size={18} />
                                    </div>
                                    <div className="chat-header-text">
                                        <span className="chat-header-title">AI Assistant</span>
                                        <span className="chat-header-subtitle">
                                            <span className={`status-dot-sm ${isOfficeHours ? '' : 'away'}`}></span>
                                            {isOfficeHours
                                                ? 'Online now'
                                                : 'AI available • Team back during business hours'
                                            }
                                        </span>
                                    </div>
                                </div>
                                <div className="chat-header-actions">
                                    <button
                                        className="chat-header-btn"
                                        onClick={handleMinimize}
                                        aria-label={isMinimized ? "Expand" : "Minimize"}
                                    >
                                        <ChevronDown size={18} className={isMinimized ? 'rotated' : ''} />
                                    </button>
                                    <button
                                        className="chat-header-btn"
                                        onClick={handleClose}
                                        aria-label="Close chat"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Body - hidden when minimized */}
                            {!isMinimized && (
                                <>
                                    {/* Messages */}
                                    <div className="chat-messages">
                                        {messages.map((msg) => (
                                            <ChatMessageBubble
                                                key={msg.id}
                                                message={msg}
                                                onFeedback={handleFeedback}
                                            />
                                        ))}

                                        {/* Lead capture form (shown inline after 3 messages) */}
                                        {showLeadForm && !leadCaptured && (
                                            <LeadCaptureForm
                                                sessionId={sessionId}
                                                onSubmitted={handleLeadSubmitted}
                                                onSkip={handleLeadSkip}
                                            />
                                        )}

                                        {isLoading && <TypingIndicator />}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Input + WhatsApp handoff */}
                                    <div className="chat-input-area">
                                        <form className="chat-input-form" onSubmit={handleSubmit}>
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                className="chat-input"
                                                value={inputValue}
                                                onChange={(e) => setInputValue(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                placeholder="Type your message..."
                                                disabled={isLoading}
                                            />
                                            <button
                                                type="submit"
                                                className="chat-send-btn"
                                                disabled={!inputValue.trim() || isLoading}
                                                aria-label="Send message"
                                            >
                                                <Send size={18} />
                                            </button>
                                        </form>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default ChatWidget;
